import axios from 'axios';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

type PublicRouteProps = {
  children: React.ReactNode;
  skipAuthCheck?: boolean;
};

/**
 * PublicRoute component - For routes that should be accessible without authentication
 * but redirect to home if the user is already authenticated
 */
function PublicRoute({ children, skipAuthCheck = false }: PublicRouteProps) {
  const [isChecking, setIsChecking] = useState(!skipAuthCheck);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const location = useLocation();

  // For password reset with token, skip auth check entirely
  const isPasswordResetWithToken =
    location.pathname === '/reset-password' &&
    location.search.includes('token=');

  useEffect(() => {
    // Skip auth check for password reset with token or if explicitly told to skip
    if (skipAuthCheck || isPasswordResetWithToken) {
      setIsChecking(false);
      return;
    }

    const checkAuth = async () => {

      try {
        // Get the base URL from environment
        const baseUrl = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER;

        // Call refresh-access endpoint directly
        await axios.post(
          `${baseUrl}/v1/auth/refresh-access`,
          {},
          {
            withCredentials: true, // Important for cookies
            timeout: 10000, // 10 second timeout
          },
        );

        setShouldRedirect(true);
      } catch {
        // User is not authenticated, show the public page
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [skipAuthCheck, isPasswordResetWithToken]);

  // Redirect to home if user is authenticated
  if (shouldRedirect) {
    return <Navigate to="/" replace />;
  }

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">
            جاري التحقق من الهوية...
          </p>
        </div>
      </div>
    );
  }

  // Show the public page
  return <>{children}</>;
}

export default PublicRoute;
